import React, { useEffect, useMemo } from 'react'

import { Skeleton } from 'antd'

import { navigate, routes } from '@redwoodjs/router'

import AICAReverseTrialStatusCard from 'src/components/AIAssistant/AICAReverseTrialStatusCard'
import AICommunityAssistantUsageFooter from 'src/components/AIAssistant/AICommunityAssistantUsageFooter'
import ContactGovWellAboutAIAssistantButton from 'src/components/AIAssistant/ContactGovWellAboutAIAssistantButton'
import ContactGovWellAboutAIAssistantModal from 'src/components/AIAssistant/ContactGovWellAboutAIAssistantModal'
import Page from 'src/components/shared/Page/Page'
import { useGetOrganizationAIDashboardSettingsQuery } from 'src/fetch/organizations'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMyOrg } from 'src/layouts/AppStateContextLayout/utils'

type Props = {
  title: string
  subtitle?: string
  customTitleDisplay?: React.ReactNode
  children: React.ReactNode
}

const AIPageContainer = ({ children, subtitle, title, customTitleDisplay }: Props) => {
  const { data: organizationAISettings, isLoading } = useGetOrganizationAIDashboardSettingsQuery()

  const aiChatIsNotEnabledModalState = useDisclosure()

  const isAIChatEnabled = useMemo(
    () => organizationAISettings?.organization?.isAIChatEnabled,
    [organizationAISettings?.organization?.isAIChatEnabled]
  )

  // The usage/upgrade footer (quota progress bar + "View pricing" link) is
  // only meaningful for legacy-trial orgs that have a hard 100-question/mo
  // ceiling. Everyone else (Lite, Premium, Pro, Enterprise, ReverseTrial,
  // Disabled) gets no footer — reverse-trial orgs already see the Premium
  // Trial status card above the page title instead.
  const org = useMyOrg()
  const showUsageFooter = org?.aiResidentAssistantUsageTier === 'AicaLegacyTrial'
  useEffect(() => {
    if (!isLoading && !isAIChatEnabled) {
      aiChatIsNotEnabledModalState.open()
    }
  }, [isAIChatEnabled, aiChatIsNotEnabledModalState, isLoading])

  return (
    <Page>
      <AICAReverseTrialStatusCard />
      <Page.Header
        title={title}
        subtitle={subtitle}
        customTitleDisplay={customTitleDisplay}
        actions={
          !isAIChatEnabled && (
            <ContactGovWellAboutAIAssistantButton
              type="primary"
              text="Contact us about the Community Assistant"
            />
          )
        }
      />
      <Page.Content>
        {isLoading && <Skeleton active />}
        {!isLoading && children}
      </Page.Content>
      {showUsageFooter && (
        <Page.Footer padding="0">
          <AICommunityAssistantUsageFooter />
        </Page.Footer>
      )}
      <ContactGovWellAboutAIAssistantModal
        isOpen={aiChatIsNotEnabledModalState.isOpen}
        onTryOutAIAssistant={() => navigate(routes.aiAssistantTrain())}
        onClose={aiChatIsNotEnabledModalState.close}
      />
    </Page>
  )
}

export default React.memo(AIPageContainer)
